// src/pages/RequestManagement/DetailsDrawer.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  IconButton,
} from "@mui/material";
import StatusDropdown from "../../../components/StatusDropdown";
import CustomText, { textStyles } from "../../../components/CustomText";
import profilecross from "../../../assets/profilecross.svg";
import CustomButton from "../../../components/CustomButton";
import changeStatus from "../../../assets/StatusIcons/changeStatus.svg";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../Utlis/Encryption";
import { BASE_URL } from "../../../components/BaseUrl";
import {toast} from "react-hot-toast";
import document1 from "../../../assets/document1.svg";
import TruncatedCell from "../../../components/TruncatedCell";
import { convertUTCToLocalDateTime } from "../../../components/dateTimeUtils";

const HeaderStyles = {
  color: "#44546F",
  fontSize: "15px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: 600,
};

const DataStyles = {
  color: "#172B4D",
  fontSize: "13px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: 400,
};

const RequestDetailsDrawer = ({ open, onClose, row ,onStatusChanged }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();


  const { company } = useSelector((state) => state.auth);
   const rawEncryptedToken = localStorage.getItem("token");
   const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  /** API stores ENUM-style values; matches the label shown in the requests table. */
  const formatRequestTypeLabel = (type) => {
    if (!type) return "-";
    const keyByType = {
      LEAVE: "options.leave",
      SCHEDULE_CHANGE: "options.scheduleChange",
      GENERIC_HR: "options.genericHR",
    };
    const i18nKey = keyByType[type];
    if (i18nKey) return t(i18nKey);
    return String(type)
      .split("_")
      .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
      .join(" ");
  };

  /** Leave reason code enum → translated label. */
  const formatReasonCodeLabel = (code) => {
    if (!code) return "-";
    const keyByCode = {
      URGENT: "options.leaveReasonCodes.urgent",
      SICK: "options.leaveReasonCodes.sick",
      VACATION: "options.leaveReasonCodes.vacation",
      OTHER: "options.leaveReasonCodes.others",
    };
    const i18nKey = keyByCode[code];
    return i18nKey ? t(i18nKey) : code;
  };

  const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const localDateTime = convertUTCToLocalDateTime(dateString, { locale: "en-GB" });
  return localDateTime === "—" ? "-" : localDateTime;
};

  const formatDateOnly = (dateString) => {
  if (!dateString) return "--";
  const localDate = convertUTCToLocalDateTime(dateString, {
    locale: "en-GB",
    formatOptions: {
      day: "2-digit",
      month: "short",
      year: "numeric",
    },
  });
  return localDate === "—" ? "--" : localDate;
};

  /** API stores days as a comma-separated string, e.g. "MON,TUE,WED". */
  const formatDays = (daysValue) => {
    if (!daysValue) return "--";
    const days = Array.isArray(daysValue)
      ? daysValue
      : String(daysValue).split(",");
    return days
      .map((day) => day.trim())
      .filter(Boolean)
      .map((day) => day.charAt(0) + day.slice(1).toLowerCase())
      .join(", ");
  };

  const formatTimeOnly = (timeString) => {
  if (!timeString) return "--";
  const localTime = convertUTCToLocalDateTime(timeString, {
    locale: "en-GB",
    formatOptions: {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    },
  });
  return localTime === "—" ? "--" : localTime;
};
  // Fetch full request details when drawer opens
  useEffect(() => {
    if (open && row?.id) {
      const fetchDetails = async () => {
        try {
          setLoading(true);
          const res = await fetch(
            `${BASE_URL}/requests/v1/requests/${row.id}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
          const data = await res.json();
          console.log("📋 Request Details Response:", data);
          setDetails(data.data);
        } catch (err) {
          console.error("Failed to fetch request details", err);
          toast.error("Failed to load request details");
        } finally {
          setLoading(false);
        }
      };
      fetchDetails();
    }
  }, [open, row, token]);

  const InfoBox = ({ children }) => (
    <Box
      mt={2}
      sx={{
        border: "1px solid #091E4224",
        borderRadius: "10px",
        px: 2,
        py: 2,
      }}
    >
      {children}
    </Box>
  );

  const InfoRow = ({ label, value }) => {
    // Handle React components and strings
    const isReactComponent = typeof value === "object" && value !== null && !(value instanceof Date);
    
    return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "flex-start" },
        mb: 2,
        gap: { xs: 0.5, sm: 2 },
      }}
    >
      <CustomText
        sx={{
          ...textStyles.h1,
          fontSize: 16,
          fontWeight: 400,
          color: "#5E5C5C",
          textAlign: "left",
            flex: { xs: "1 1 100%", sm: "0 0 40%" },
        }}
      >
        {label}
      </CustomText>
        <Box
          sx={{
            flex: { xs: "1 1 100%", sm: "1 1 60%" },
            width: { xs: "100%", sm: "auto" },
            textAlign: { xs: "left", sm: "right" },
            display: "flex",
            justifyContent: { xs: "flex-start", sm: "flex-end" },
            alignItems: "flex-start",
          }}
        >
          {isReactComponent ? (
            <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" }, width: "100%" }}>
              {value}
            </Box>
          ) : (
      <CustomText
        sx={{
          ...textStyles.h1,
          fontSize: 15,
          fontWeight: 400,
          color: "#172B4D",
          textAlign: { xs: "left", sm: "right" },
          wordBreak: "break-word",
          width: "100%",
        }}
      >
        {value || "--"}
      </CustomText>
          )}
        </Box>
    </Box>
  );
  };

  if (!row) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420, md: 500 },
          maxWidth: "100vw",
          backgroundColor: "white",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#E7EBEE",
          p: 2,
          py: 1,
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <IconButton onClick={onClose} sx={{ flexShrink: 0 }}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              fontSize: 20,
              fontWeight: 600,
              color: "#2C384C",
              fontFamily: "Poppins, sans-serif",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t("requestDetails.title")}   #{row.id}
          </CustomText>
        </Box>
      </Box>

      <Box sx={{ px: 2, pb: 2, mt: 2 }}>
        {loading ? (
          <CustomText sx={{fontSize:"14px"}}>  {t("requestDetails.loading")}</CustomText>
        ) : details ? (
          <>
            <InfoBox>
              <InfoRow
                label={t("requestDetails.status")}
                value={
                  <Box sx={{ pointerEvents: "none" }}>
                    <StatusDropdown currentStatus={details.status} />
                  </Box>
                }
              />

{details.status?.toUpperCase() === "APPROVED" && (
   <InfoRow
                 label={t("requestDetails.approvedAt")}
                // value={details.leave_start_date}
                value={formatDateTime(details.decided_at)}

              />)}
          
          {["APPROVED", "REJECTED", "INFO_REQUESTED"].includes(details.status?.toUpperCase()) && (
  <InfoRow
    label={t("requestDetails.commentsByAdmin")}
    value={<TruncatedCell text={details.admin_comment || "-"} />}
  />
)}
</InfoBox>


                  <InfoBox>
    <InfoRow
                label={t("requestDetails.requested")}
                value={formatDateOnly(details.created_at)}
              />

              {(details.requester_name || details.requester_full_name || details.employee_name) && (
                <InfoRow
                  label={t("requestManagement.tableHeaders.employeeName") || "Employee Name"}
                  value={details.requester_name || details.requester_full_name || details.employee_name}
                />
              )}

              <InfoRow
                label={t("requestDetails.type")}
                value={formatRequestTypeLabel(details.type)}
              />

              <InfoRow
                label={t("labels.subject")}
                value={details.subject || "--"}
              />

              {/* LEAVE Request Details */}
              {details.type === "LEAVE" && (
                <>
                  <InfoRow
                    label={t("requestDetails.reasonCode") || "Leave Reason Code"}
                    value={formatReasonCodeLabel(details.leave_reason_code)}
                  />
                  {details.leave_reason_text && (
                    <InfoRow
                      label={t("requestDetails.reason") || "Reason"}
                      value={<TruncatedCell text={details.leave_reason_text} />}
                    />
                  )}
                  <InfoRow
                    label={t("labels.startDate")}
                    value={formatDateOnly(details.leave_start_date)}
                  />
                  <InfoRow
                    label={t("labels.endDate")}
                    value={formatDateOnly(details.leave_end_date)}
                  />
                  <InfoRow
                    label={t("labels.partialDay")}
                    value={details.leave_partial ? "Yes" : "No"}
                  />
                  {details.leave_partial && (
                    <>
                      {details.leave_partial_start && (
                        <InfoRow
                          label={t("labels.partialStart")}
                          value={formatTimeOnly(details.leave_partial_start)}
                        />
                      )}
                      {details.leave_partial_end && (
                        <InfoRow
                          label={t("labels.partialEnd")}
                          value={formatTimeOnly(details.leave_partial_end)}
                        />
                      )}
                    </>
                  )}
                </>
              )}

              {/* SCHEDULE_CHANGE Request Details */}
              {details.type === "SCHEDULE_CHANGE" && (
                <>
                  <InfoRow
                    label={t("labels.currentDays")}
                    value={formatDays(details.sched_current_days)}
                  />
                  <InfoRow
                    label={t("labels.currentStartTime")}
                    value={formatTimeOnly(details.sched_current_start_time)}
                  />
                  <InfoRow
                    label={t("labels.currentEndTime")}
                    value={formatTimeOnly(details.sched_current_end_time)}
                  />
                  <InfoRow
                    label={t("labels.effectiveFrom")}
                    value={formatDateOnly(details.sched_effective_from)}
                  />
                  {details.sched_reason_text && (
                    <InfoRow
                      label={t("labels.details")}
                      value={<TruncatedCell text={details.sched_reason_text} />}
                    />
                  )}
                </>
              )}

              {/* GENERIC_HR Request Details */}
              {details.type === "GENERIC_HR" && (
                <>
                  <InfoRow
                    label={t("labels.details")}
                    value={details.details ? <TruncatedCell text={details.details} /> : "--"}
                  />
                </>
              )}

              {details.status?.toUpperCase() === "APPROVED" && (
              <InfoRow
               label={t("requestDetails.approvedBy")}
                value={details.decided_by_name}
              />
              )}
            </InfoBox>
{details.file_url && (
           <CustomText
      sx={{
        fontSize: 18,
        fontWeight: 500,
        color: "#2C384C",
        mb: 1,
        mt:2,
        fontFamily: "Poppins, sans-serif",
        textAlign:"left"
      }}
    >
      {t("requestDetails.requestDocument") || "Request Document"}
    </CustomText>
)}

{details.file_url && (
  <Box
    sx={{
      mt: 3,
      border: "1px solid #091E4224",
      borderRadius: "10px",
      p: 2,
    }}
  >
  

{(() => {
  const fileUrl = details.file_url || "";
  const fileName = details.file_name || "";
  const isPdf = fileUrl && (
    fileUrl.toLowerCase().endsWith(".pdf") || 
    fileUrl.toLowerCase().includes(".pdf") ||
    fileUrl.toLowerCase().includes("application/pdf") ||
    fileName.toLowerCase().endsWith(".pdf") ||
    fileName.toLowerCase().includes(".pdf")
  );

  console.log("📄 File Debug:", { fileUrl, fileName, isPdf });

  return isPdf ? (
  // 📄 Show PDF icon and name
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      background: "#f9f9f9",
      borderRadius: "6px",
      p: 2,
      width: "100%",
        minHeight: 200,
      textAlign: "center",
    }}
  >
    <img
      src={document1}
      alt="PDF Document"
      style={{ width: 60, height: 60, marginBottom: 8 }}
    />
    <CustomText
      sx={{
        fontSize: 14,
        fontWeight: 400,
        color: "#172B4D",
        textAlign: "center",
        wordBreak: "break-all",
          mb: 2,
      }}
    >
        {details.file_name || fileUrl.split("/").pop()}
    </CustomText>
      <Box sx={{ mt: 2, width: "100%", display: "flex", justifyContent: "center" }}>
        <CustomButton
          label={t("requestDetails.viewDocument") || "View Document"}
          size="small"
          variant="contained"
          onClick={() => {
            console.log("🔗 Opening PDF:", fileUrl);
            if (fileUrl) {
              window.open(fileUrl, "_blank", "noopener,noreferrer");
            } else {
              console.error("❌ No file URL available");
            }
          }}
          sx={{
            minWidth: 140,
            fontSize: "13px",
            textTransform: "none",
          }}
        />
      </Box>
  </Box>
) : (
  // 🖼️ Image preview
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      background: "#f9f9f9",
      borderRadius: "6px",
      overflow: "hidden",
      width: "100%",
      minHeight: 200,
    }}
  >
    <img
      crossOrigin="anonymous"
      src={details.file_url}
      alt="Request Document"
      style={{
        maxWidth: "100%",
        maxHeight: "250px",
        objectFit: "contain",
      }}
      onError={(e) => {
        e.target.src =
          "https://cdn-icons-png.flaticon.com/512/337/337946.png"; // fallback icon
      }}
    />
    {details.file_name && (
      <CustomText
        sx={{
          fontSize: 12,
          fontWeight: 400,
          color: "#172B4D",
          textAlign: "center",
          wordBreak: "break-all",
          mt: 1,
        }}
      >
        {details.file_name}
      </CustomText>
    )}
    {/* Always show view button for images too */}
    <Box sx={{ mt: 2, width: "100%", display: "flex", justifyContent: "center" }}>
      <CustomButton
        label={t("requestDetails.viewDocument") || "View Document"}
        size="small"
        variant="contained"
        onClick={() => {
          if (details.file_url) {
            window.open(details.file_url, "_blank", "noopener,noreferrer");
          }
        }}
        sx={{
          minWidth: 140,
          fontSize: "13px",
          textTransform: "none",
        }}
      />
    </Box>
  </Box>
  );
})()}
  </Box>
)} 
          </>
        ) : (
          <CustomText>{t("requestDetails.noDetails")}</CustomText>
        )}
      </Box>

 


    </Drawer>
  );
};

export default RequestDetailsDrawer;



